import { takeLatest, call, select, put } from 'redux-saga/effects';
import { addPaddingZerosToNum } from 'utils/functions/general';
import {
  getDateTimeObjFromDateTimeStr,
  getDateDetailsFromDateTimeObj,
  getTimeDetailsFromDateTimeObj,
  getElapsedTimeFromDateTimeObj,
  checkIfTwoDatesAreEqual,
} from 'utils/functions/dateTime';
import { status } from 'constants/general';
import {
  getAllLabs,
  getUsers,
  getLabsCatalog,
  searchLabsCatalog,
  getUploadedContent,
} from 'modules/labs/common/api';
import {
  fetchAllLabsSuccess,
  fetchAllLabsFail,
  fetchAllChemistsSuccess,
  fetchAllChemistsFail,
  fetchAllUsersSuccess,
  fetchAllUsersFail,
} from 'modules/labs/common/actions';
import { userRoleEnum } from 'modules/labs/common/enums';
import {
  getAllOrders,
  getOrderDetails,
  assignOrderToEntity,
  updateOrderServices,
  getOrderPatientUserDetails,
  updateOrderStatus,
  updateOrderNote,
  scheduleOrder,
  getScheduleOrderReasons,
  getCancelOrderReasons,
} from './api';
import {
  fetchAllOrdersSuccess,
  fetchAllOrdersFail,
  setAllOrdersAssignedEntityDetails,
  fetchOrderDetailsSuccess,
  fetchOrderDetailsFail,
  setOrderPatientUserDetails,
  setOrderAssignedEntityDetails,
  setOrderLogsDetails,
  setOrderServicesDetails,
  setOrderResults,
  fetchAssignOrderToEntitySuccess,
  fetchAssignOrderToEntityFail,
  fetchOrderServicesSuggestionsSuccess,
  fetchOrderServicesSuggestionsFail,
  fetchOrderServicesSubmissionSuccess,
  fetchOrderServicesSubmissionFail,
  fetchOrderStatusChangeSubmissionSuccess,
  fetchOrderStatusChangeSubmissionFail,
  fetchOrderNoteChangeSubmissionSuccess,
  fetchOrderNoteChangeSubmissionFail,
  fetchScheduleOrderSubmissionSuccess,
  fetchScheduleOrderSubmissionFail,
  fetchScheduleOrderReasonsSuccess,
  fetchScheduleOrderReasonsFail,
  fetchCancelOrderReasonsSuccess,
  fetchCancelOrderReasonsFail,
} from './actions';
import ordersActionTypes from './action-types';
import { orderStatusEnum, orderStatusToShowUploadedResultsSection } from './enums';
import ordersStrings from './strings';

const getCreationDateDetails = orderDate => {
  const { dateObj } = getDateTimeObjFromDateTimeStr(orderDate, true, true);
  const { day, month, year } = getDateDetailsFromDateTimeObj(dateObj);
  const { hours, minutes, period } = getTimeDetailsFromDateTimeObj(dateObj);
  const { elapsedMinutes, elapsedHours } = getElapsedTimeFromDateTimeObj(dateObj);

  const orderDateTimeStr = `${day} ${month} ${year}, ${hours}:${addPaddingZerosToNum(
    minutes,
    1,
    2,
  )} ${period}`;
  const orderElapsedTimeStr = ordersStrings.ordersList.orderGridItemElapsed(
    elapsedHours,
    addPaddingZerosToNum(elapsedMinutes, 1, 2),
  );

  return {
    orderDateTimeStr,
    orderElapsedTimeStr,
  };
};

const checkIfOrderCreationDateAndDeliveryDateIsTheSame = (creationDate, deliveryDate) => {
  const { dateObj: creationDateObj } = getDateTimeObjFromDateTimeStr(creationDate, true, true);
  const { dateObj: deliveryDateObj } = getDateTimeObjFromDateTimeStr(deliveryDate, true, true);
  return checkIfTwoDatesAreEqual(creationDateObj, deliveryDateObj);
};

const mapOrderItemObjForView = order => {
  const { orderDateTimeStr, orderElapsedTimeStr } = getCreationDateDetails(order.createdOn);
  const orderStatusStr = orderStatusEnum.getKeyByValue(order.orderStateTypeId);

  return {
    ...order,
    orderDateTimeStr,
    orderElapsedTimeStr,
    orderStatusStr,
  };
};

function* getAllOrdersSaga({ payload }) {
  try {
    const response = yield call(getAllOrders, payload);

    const orderListData = response.data.orderDTOs;
    const mappedOrdersList = [];

    if (orderListData) {
      orderListData.forEach(order => {
        mappedOrdersList.push(mapOrderItemObjForView(order));
      });
    }

    yield put(fetchAllOrdersSuccess({ ...response.data, mappedOrdersList }));

    yield put(setAllOrdersAssignedEntityDetails(mappedOrdersList));
  } catch (error) {
    yield put(fetchAllOrdersFail());
  }
}

function* setAllOrdersAssignedEntityDetailsSaga({ payload }) {
  try {
    const allLabsState = state => state.common.labsList;
    const allChemistsState = state => state.common.chemistsList;
    const allLabs = yield select(allLabsState);
    const allChemists = yield select(allChemistsState);

    let labsList = allLabs.details;
    let chemistsData = allChemists.details;
    let chemistsList = chemistsData.results || [];

    if (labsList.length === 0 && chemistsList.length === 0) {
      const allLabsResponse = yield call(getAllLabs);
      labsList = allLabsResponse.data;

      const allChemistsBody = {
        data: {
          roleId: userRoleEnum.Chemist,
          searchTerm: '',
          deleted: false,
          pageNumber: 1,
          pageSize: 10000,
        },
      };
      const allChemistsResponse = yield call(getUsers, allChemistsBody);
      chemistsData = allChemistsResponse.data;
      chemistsList = chemistsData.results;
    }

    const orderListData = payload;
    const mappedOrdersList = [];

    if (orderListData) {
      orderListData.forEach(order => {
        const orderLab = labsList.find(lab => lab.key === order.assignedLocation);
        const orderChemist = chemistsList.find(chemist => chemist.key === order.assignedAgent);

        mappedOrdersList.push({
          ...order,
          selectedLabObj: {
            key: orderLab?.key,
            name: orderLab?.name,
          },
          selectedChemistObj: {
            key: orderChemist?.key,
            name: orderChemist?.fullName,
          },
        });
      });
    }

    const allOrdersState = state => state.labsOrders.ordersList;
    const allOrders = yield select(allOrdersState);
    yield put(fetchAllOrdersSuccess({ ...allOrders.details, mappedOrdersList }));
    yield put(fetchAllLabsSuccess(labsList));
    yield put(fetchAllChemistsSuccess(chemistsData));
  } catch (error) {
    yield put(fetchAllLabsFail());
    yield put(fetchAllChemistsFail());
  }
}

function* getOrderDetailsSaga({ payload }) {
  try {
    const response = yield call(getOrderDetails, payload);
    const orderDetails = response.data;
    const { orderDateTimeStr, orderElapsedTimeStr } = getCreationDateDetails(
      orderDetails.createdOn,
    );
    const { orderDateTimeStr: orderScheduledDateTimeStr } = getCreationDateDetails(
      orderDetails.deliveredOn,
    );
    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails,
        orderDate: {
          orderDateTimeStr,
          orderElapsedTimeStr,
          orderScheduledDateTimeStr,
          isOrderCreationDateSameAsDeliveryDate: checkIfOrderCreationDateAndDeliveryDateIsTheSame(
            orderDetails.createdOn,
            orderDetails.deliveredOn,
          ),
        },
        selectedLabObj: status.FETCHING,
        selectedChemistObj: status.FETCHING,
        mappedServicesList: status.FETCHING,
        mappedServicesListSnapshot: status.FETCHING,
        patientUserDetails: status.FETCHING,
        attachedResults: status.FETCHING,
      }),
    );

    yield put(setOrderPatientUserDetails(orderDetails.userKey));
    yield put(
      setOrderAssignedEntityDetails({
        assignedLab: orderDetails.assignedLocation,
        assignedChemist: orderDetails.assignedAgent,
      }),
    );
    yield put(setOrderLogsDetails());
    yield put(
      setOrderServicesDetails({
        orderServices: orderDetails.orderItems,
        orderServicesSnapshot: orderDetails.orderItemsSnapshot,
      }),
    );
    if (orderStatusToShowUploadedResultsSection.includes(orderDetails.orderStateTypeId))
      yield put(setOrderResults(orderDetails.key));
  } catch (error) {
    yield put(fetchOrderDetailsFail());
  }
}

function* setOrderPatientUserDetailsSaga({ payload }) {
  try {
    const patientUser = {
      patientUserKey: payload,
    };
    const response = yield call(getOrderPatientUserDetails, patientUser);

    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);
    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        patientUserDetails: response.data,
      }),
    );
  } catch (error) {
    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);

    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        patientUserDetails: status.FAIL,
      }),
    );
  }
}

function* setOrderAssignedEntityDetailsSaga({ payload }) {
  try {
    const allLabsResponse = yield call(getAllLabs);

    const labsList = allLabsResponse.data;

    const selectedLabObj = {
      key: '0',
      name: ordersStrings.orderDetailsContent.orderLabDropdownPlaceholder,
    };
    const selectedChemistObj = {
      key: '0',
      name: ordersStrings.orderDetailsContent.orderChemistDropdownPlaceholder,
    };
    if (labsList.length > 0) {
      const { assignedLab, assignedChemist } = payload;

      const orderLab = labsList.find(lab => lab.key === assignedLab);

      let orderChemist;
      if (orderLab) {
        const labChemistsBody = {
          data: {
            pharmacyKey: orderLab.key,
            roleId: userRoleEnum.Chemist,
            active: true,
            deleted: false,
            pageNumber: 1,
            pageSize: 10000,
          },
        };
        const labChemistsResponse = yield call(getUsers, labChemistsBody);
        const orderLabChemists = labChemistsResponse.data.results;
        orderChemist = orderLabChemists.find(chemist => chemist.key === assignedChemist);
      }

      if (orderLab) {
        selectedLabObj.key = orderLab.key;
        selectedLabObj.name = orderLab.name;
      }
      if (orderChemist) {
        selectedChemistObj.key = orderChemist.key;
        selectedChemistObj.name = orderChemist.fullName;
      }
    }

    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);
    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        selectedLabObj,
        selectedChemistObj,
      }),
    );
    yield put(fetchAllLabsSuccess(labsList));
  } catch (error) {
    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);

    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        selectedLabObj: status.FAIL,
        selectedChemistObj: status.FAIL,
      }),
    );
    yield put(fetchAllLabsFail());
  }
}

function* setOrderLogsDetailsSaga() {
  try {
    const allChemistsBody = {
      data: {
        roleId: userRoleEnum.Chemist,
        searchTerm: '',
        deleted: false,
        pageNumber: 1,
        pageSize: 10000,
      },
    };
    const allChemistsResponse = yield call(getUsers, allChemistsBody);

    const userRoles = Object.keys(userRoleEnum).filter(
      userRoleKey => userRoleKey !== 'Courier' && userRoleKey !== 'PAPIUser',
    );
    let allUsersQuery = userRoles.map(role => `roleId=${userRoleEnum[role]}`).join('&');
    allUsersQuery = `${allUsersQuery}&searchTerm=&pageNumber=1&pageSize=10000`;
    const allUsersBody = {
      query: allUsersQuery,
    };
    const allUsersResponse = yield call(getUsers, allUsersBody);

    yield put(fetchAllChemistsSuccess(allChemistsResponse.data));
    yield put(fetchAllUsersSuccess(allUsersResponse.data));
  } catch (error) {
    yield put(fetchAllChemistsFail());
    yield put(fetchAllUsersFail());
  }
}

function* setOrderServicesDetailsSaga({ payload }) {
  try {
    const { orderServices, orderServicesSnapshot } = payload;

    const allOrderServices = [...orderServices, ...orderServicesSnapshot].map(
      service => service.productKey,
    );
    const allUniqueOrderServices = [...new Set(allOrderServices)];
    const mappedServicesList = [];
    const mappedServicesListSnapshot = [];

    if (allUniqueOrderServices.length > 0) {
      const servicesQuery = allUniqueOrderServices
        .map(service => `serviceKeys=${service}`)
        .join('&');
      const labsCatalogBody = {
        query: servicesQuery,
        headers: {
          Language: 'en-US',
        },
      };
      const labsCatalogResponse = yield call(getLabsCatalog, labsCatalogBody);
      const OrderServicesList = labsCatalogResponse.data.Data.Services;

      if (OrderServicesList.length > 0) {
        orderServices.forEach(orderService => {
          const orderServiceObj = OrderServicesList.find(
            serviceItem => serviceItem.ServiceKey === orderService.productKey,
          );

          if (orderServiceObj)
            mappedServicesList.push({
              serviceKey: orderServiceObj.ServiceKey,
              serviceName: orderServiceObj.ServiceTranslators.find(
                service => service.Culture === 'en-US',
              ).Name,
              servicePrice: orderServiceObj.ServicePrice,
            });
        });

        orderServicesSnapshot.forEach(orderService => {
          const orderServiceObj = OrderServicesList.find(
            serviceItem => serviceItem.ServiceKey === orderService.productKey,
          );

          if (orderServiceObj)
            mappedServicesListSnapshot.push({
              serviceKey: orderServiceObj.ServiceKey,
              serviceName: orderServiceObj.ServiceTranslators.find(
                service => service.Culture === 'en-US',
              ).Name,
              servicePrice: orderServiceObj.ServicePrice,
            });
        });
      }
    }

    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);
    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        mappedServicesList,
        mappedServicesListSnapshot,
      }),
    );
  } catch (error) {
    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);

    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        mappedServicesList: status.FAIL,
        mappedServicesListSnapshot: status.FAIL,
      }),
    );
  }
}

function* setOrderResultsSaga({ payload }) {
  try {
    const body = {
      orderId: payload,
    };
    const response = yield call(getUploadedContent, body);

    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);
    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        attachedResults: response.data.data,
      }),
    );
  } catch (error) {
    const orderDetailsState = state => state.labsOrders.orderDetails;
    const orderDetails = yield select(orderDetailsState);

    yield put(
      fetchOrderDetailsSuccess({
        ...orderDetails.details,
        attachedResults: status.FAIL,
      }),
    );
  }
}

function* assignOrderToEntitySaga({ payload }) {
  try {
    const response = yield call(assignOrderToEntity, payload);
    yield put(fetchAssignOrderToEntitySuccess(response));
  } catch (error) {
    yield put(fetchAssignOrderToEntityFail());
  }
}

function* fetchOrderServicesSuggestionsSaga({ payload }) {
  try {
    const response = yield call(searchLabsCatalog, payload);
    yield put(fetchOrderServicesSuggestionsSuccess(response.data?.Data?.Services));
  } catch (error) {
    yield put(fetchOrderServicesSuggestionsFail(error));
  }
}

function* fetchOrderServicesSubmissionSaga({ payload }) {
  try {
    const { callbackAction } = payload;

    const response = yield call(updateOrderServices, payload);
    callbackAction();
    yield put(fetchOrderServicesSubmissionSuccess(response.data));
  } catch (error) {
    yield put(fetchOrderServicesSubmissionFail(error));
  }
}

function* fetchOrderStatusChangeSubmissionSaga({ payload }) {
  const { callbackAction } = payload;

  try {
    const response = yield call(updateOrderStatus, payload);
    callbackAction(true);
    yield put(fetchOrderStatusChangeSubmissionSuccess(response.data));
  } catch (error) {
    callbackAction();
    yield put(fetchOrderStatusChangeSubmissionFail(error));
  }
}

function* fetchOrderNoteChangeSubmissionSaga({ payload }) {
  try {
    const response = yield call(updateOrderNote, payload);
    yield put(fetchOrderNoteChangeSubmissionSuccess(response.data));
  } catch (error) {
    yield put(fetchOrderNoteChangeSubmissionFail(error));
  }
}

function* fetchScheduleOrderSubmissionSaga({ payload }) {
  const { callbackAction } = payload;

  try {
    const response = yield call(scheduleOrder, payload);
    callbackAction(true);
    yield put(fetchScheduleOrderSubmissionSuccess(response.data));
  } catch (error) {
    callbackAction();
    yield put(fetchScheduleOrderSubmissionFail(error));
  }
}

function* fetchScheduleOrderReasonsSaga() {
  try {
    const response = yield call(getScheduleOrderReasons);
    yield put(fetchScheduleOrderReasonsSuccess(response.data[0].orderStateChildReasons));
  } catch (error) {
    yield put(fetchScheduleOrderReasonsFail(error));
  }
}

function* fetchCancelOrderReasonsSaga() {
  try {
    const response = yield call(getCancelOrderReasons);
    yield put(fetchCancelOrderReasonsSuccess(response.data));
  } catch (error) {
    yield put(fetchCancelOrderReasonsFail(error));
  }
}

function* ordersSagas() {
  yield takeLatest(ordersActionTypes.FETCH_ALL_ORDERS, getAllOrdersSaga);
  yield takeLatest(
    ordersActionTypes.SET_ALL_ORDERS_ASSIGNED_ENTITY_DETAILS,
    setAllOrdersAssignedEntityDetailsSaga,
  );
  yield takeLatest(ordersActionTypes.FETCH_ORDER_DETAILS, getOrderDetailsSaga);
  yield takeLatest(
    ordersActionTypes.SET_ORDER_PATIENT_USER_DETAILS,
    setOrderPatientUserDetailsSaga,
  );
  yield takeLatest(
    ordersActionTypes.SET_ORDER_ASSIGNED_ENTITY_DETAILS,
    setOrderAssignedEntityDetailsSaga,
  );
  yield takeLatest(ordersActionTypes.SET_ORDER_LOGS_DETAILS, setOrderLogsDetailsSaga);
  yield takeLatest(ordersActionTypes.SET_ORDER_SERVICES_DETAILS, setOrderServicesDetailsSaga);
  yield takeLatest(ordersActionTypes.SET_ORDER_RESULTS, setOrderResultsSaga);
  yield takeLatest(ordersActionTypes.FETCH_ASSIGN_ORDER_TO_ENTITY, assignOrderToEntitySaga);
  yield takeLatest(
    ordersActionTypes.FETCH_ORDER_SERVICES_SUGGESTIONS,
    fetchOrderServicesSuggestionsSaga,
  );
  yield takeLatest(
    ordersActionTypes.FETCH_ORDER_SERVICES_SUBMISSION,
    fetchOrderServicesSubmissionSaga,
  );
  yield takeLatest(
    ordersActionTypes.FETCH_ORDER_STATUS_CHANGE_SUBMISSION,
    fetchOrderStatusChangeSubmissionSaga,
  );
  yield takeLatest(
    ordersActionTypes.FETCH_ORDER_NOTE_CHANGE_SUBMISSION,
    fetchOrderNoteChangeSubmissionSaga,
  );
  yield takeLatest(
    ordersActionTypes.FETCH_SCHEDULE_ORDER_SUBMISSION,
    fetchScheduleOrderSubmissionSaga,
  );
  yield takeLatest(ordersActionTypes.FETCH_SCHEDULE_ORDER_REASONS, fetchScheduleOrderReasonsSaga);
  yield takeLatest(ordersActionTypes.FETCH_CANCEL_ORDER_REASONS, fetchCancelOrderReasonsSaga);
}

export default ordersSagas;
